import * as React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { ButtonBase, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, useMediaQuery, useTheme } from '@mui/material';
import { mediaQuery } from '../../theme';
import { StatusChip } from '../ui';
import { formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

const ROWS_PER_PAGE = [25, 50, 100, 500];

interface Props {
    submissions: any[];
    paging: any;
    pagingUpdate: (params: any) => void;
    typesMap: Record<number, string>;
}

const detailsPath = (submission: any) => `/customer-form-submissions/${submission.id}`;

/** Row click is a mouse convenience only: it must not steal a text selection or a click on a link/button inside the row. */
const isPlainRowClick = (event: React.MouseEvent<HTMLElement>): boolean => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, [role="button"]')) return false;
    return !(window.getSelection?.()?.toString());
};

/** Form submissions: a table on desktop, one card per submission under 768px. */
export default function CustomerFormSubmissionsTable({ submissions, paging, pagingUpdate, typesMap }: Props) {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const phone = useMediaQuery(mediaQuery(theme.custom.mq.sm));

    const typeLabel = (submission: any) => typesMap[submission.typeId] || submission.typeLabel || `Type #${submission.typeId}`;
    const processedChip = (submission: any) =>
        submission.processed ? <StatusChip tone="success" size="sm">Processed</StatusChip> : <StatusChip tone="warning" size="sm">Unprocessed</StatusChip>;

    const pagination = (
        <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE}
            component="div"
            count={paging.total || 0}
            rowsPerPage={paging.limit || 50}
            page={Math.max(0, (paging.page || 1) - 1)}
            onPageChange={(_, newPage) => pagingUpdate({ ...paging, page: newPage + 1 })}
            onRowsPerPageChange={(event) => pagingUpdate({ ...paging, page: 1, limit: +event.target.value })}
            labelRowsPerPage={phone ? 'Rows' : 'Rows per page'}
        />
    );

    if (phone) {
        return (
            <>
                <div className={classes.cards}>
                    {submissions.map((submission) => (
                        <ButtonBase key={submission.id} component="div" className={classes.card} onClick={() => navigate(detailsPath(submission))}>
                            <div className={classes.cardTop}>
                                <span className={classes.idLink}>#{submission.id}</span>
                                {processedChip(submission)}
                            </div>
                            <div className={classes.email}>{submission.email || '—'}</div>
                            <div className={classes.cardMeta}>
                                <span>{typeLabel(submission)}</span>
                                <span>{formatDate(submission.createdAt, { time: true })}</span>
                            </div>
                        </ButtonBase>
                    ))}
                </div>
                {pagination}
            </>
        );
    }

    return (
        <>
            <TableContainer className={classes.container}>
                <Table stickyHeader size="small" aria-label="Form submissions">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Form type</TableCell>
                            <TableCell>Processed</TableCell>
                            <TableCell>Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {submissions.map((submission) => (
                            <TableRow
                                key={submission.id}
                                hover
                                className={classes.row}
                                onClick={(event) => {
                                    if (isPlainRowClick(event)) navigate(detailsPath(submission));
                                }}
                            >
                                <TableCell>
                                    <Link to={detailsPath(submission)} className={classes.idLink}>#{submission.id}</Link>
                                </TableCell>
                                <TableCell className={classes.email}>{submission.email || '—'}</TableCell>
                                <TableCell>{typeLabel(submission)}</TableCell>
                                <TableCell>{processedChip(submission)}</TableCell>
                                <TableCell className={classes.date}>{formatDate(submission.createdAt, { time: true })}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination}
        </>
    );
}
